import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

export function HostCalendar({ trips, listings }) {
  const { t: tr } = useTranslation();
  const MONTHS = tr("calendar.months", { returnObjects: true });
  const DOW = tr("calendar.dow", { returnObjects: true });
  const now = new Date();
  const [cur, setCur] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const today = ymd(now.getFullYear(), now.getMonth(), now.getDate());
  const startDow = new Date(cur.y, cur.m, 1).getDay();
  const daysIn = new Date(cur.y, cur.m + 1, 0).getDate();

  const tripOn = (ds) => trips.filter((t) => t.pickup_date <= ds && ds <= t.return_date);
  const blockOn = (ds) => {
    const out = [];
    for (const c of listings) for (const b of c.blocks || []) if (b.start <= ds && ds <= b.end) out.push({ car: c.name, ...b });
    return out;
  };

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);

  const move = (delta) => {
    let m = cur.m + delta, y = cur.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setCur({ y, m });
  };

  return (
    <div data-testid="host-calendar" className="bg-white rounded-2xl shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold">{MONTHS[cur.m]} {cur.y}</h3>
        <div className="flex gap-1">
          <button onClick={() => move(-1)} data-testid="cal-prev" className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500"><ChevronLeft size={18} /></button>
          <button onClick={() => move(1)} data-testid="cal-next" className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {DOW.map((d) => <div key={d} className="text-[10px] font-semibold text-slate-400 py-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const ds = ymd(cur.y, cur.m, d);
          const t = tripOn(ds), b = blockOn(ds);
          const isToday = ds === today;
          const bg = t.length ? "bg-blue-600 text-white" : b.length ? "bg-amber-100 text-amber-700" : "text-slate-600";
          return (
            <div key={ds} title={t.length ? t.map((x) => `${x.guest} · ${x.car_name}`).join("\n") : b.length ? b.map((x) => tr("hostBlocks.blockedTooltip", { car: x.car })).join("\n") : ""}
              className={`aspect-square rounded-lg grid place-items-center text-xs font-medium ${bg} ${isToday && !t.length ? "ring-2 ring-blue-400" : ""}`}>
              {d}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-600" /> {tr("hostBlocks.booked")}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> {tr("hostBlocks.blocked")}</span>
      </div>
    </div>
  );
}
